import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const TenantSwitcher: React.FC = () => {
  const { tenants, currentTenant, switchTenant } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSwitch = async (tenant: typeof tenants[0]) => {
    if (currentTenant?.id === tenant.id) {
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await switchTenant(tenant.id);
      if (result.success) {
        // Navigate to the same relative path but with the new tenant slug
        // Extract the path after /t/{currentSlug}/
        let relativePath = '/';
        
        if (tenantSlug && location.pathname.startsWith(`/t/${tenantSlug}`)) {
          // Get the path after the tenant slug
          relativePath = location.pathname.slice(`/t/${tenantSlug}`.length) || '/';
        }
        
        // Navigate to the new tenant with the same relative path
        navigate(`/t/${tenant.shopify_shop_name}${relativePath}`);
      }
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  // Don't render if no tenants available
  if (tenants.length === 0) {
    return (
      <div className="tenant-switcher">
        <div className="tenant-switcher-button disabled">
          <span className="tenant-icon">🏪</span>
          <span className="tenant-name">No stores available</span>
        </div>
      </div>
    );
  }

  // Single tenant - just display it
  if (tenants.length === 1) {
    return (
      <div className="tenant-switcher">
        <div className="tenant-switcher-button disabled">
          <span className="tenant-icon">🏪</span>
          <span className="tenant-name">{currentTenant?.name || 'Select store'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-switcher" ref={dropdownRef}>
      <button
        className="tenant-switcher-button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
      >
        <span className="tenant-icon">🏪</span>
        <span className="tenant-name">
          {isLoading ? 'Switching...' : (currentTenant?.name || 'Select store')}
        </span>
        <span className={`tenant-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="tenant-dropdown">
          <div className="tenant-dropdown-header">Switch Store</div>
          {tenants.map((tenant) => (
            <button
              key={tenant.id}
              className={`tenant-dropdown-item ${tenant.id === currentTenant?.id ? 'active' : ''}`}
              onClick={() => handleSwitch(tenant)}
              disabled={isLoading}
            >
              <span className="tenant-item-name">{tenant.name}</span>
              <span className="tenant-item-shop">{tenant.shopify_shop_name}</span>
              {tenant.id === currentTenant?.id && <span className="tenant-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;
